import { useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import { useWelcomeIntroGate } from "@/hooks/onboarding/useWelcomeIntroGate";
import { WelcomeIntroDialog } from "@/components/onboarding/WelcomeIntroDialog";
import { WaitlistDialog } from "@/components/auth/WaitlistDialog";
import { useMultiWealthica2Store } from "@/stores/multiWealthica2Store";

export function WelcomeIntroGate() {
  const { shouldShow, forceOpen, isLoading, submit, dismiss, userEmail } = useWelcomeIntroGate();
  const [open, setOpen] = useState(false);
  const [waitlistOpen, setWaitlistOpen] = useState(false);
  const [waitlistPersona, setWaitlistPersona] = useState<'investor' | 'advisor' | 'asset_manager' | undefined>(undefined);
  const location = useLocation();

  const isOAuthCallback = useMemo(() => (
    location.pathname === '/investments/connect' && (location.search.includes('code=') || location.search.includes('error='))
  ), [location.pathname, location.search]);

  // Gate dialog while any connection is processing/persisting
  const connectionsMap = useMultiWealthica2Store(s => s.connections);
  const isAnyProcessing = useMemo(() => {
    try {
      for (const conn of connectionsMap.values()) {
        const c: any = conn as any;
        if (c?.isPersisting || c?.processingState === 'processing') return true;
      }
    } catch {}
    return false;
  }, [connectionsMap]);

  useEffect(() => {
    if (isOAuthCallback) { setOpen(false); return; }
    if (isLoading) return;
    if (isAnyProcessing) { setOpen(false); return; }
    setOpen(!!(shouldShow || forceOpen));
  }, [isOAuthCallback, isLoading, shouldShow, forceOpen, isAnyProcessing]);

  const handleOpenChange = async (nextOpen: boolean) => {
    if (!nextOpen) {
      if (isOAuthCallback || isAnyProcessing) {
        setOpen(false);
        return;
      }
      const outcome = await dismiss();
      if (outcome === 'waitlist') {
        setWaitlistOpen(true);
      }
    }
    setOpen(nextOpen);
  };

  const handleSubmit = async (answers: any) => {
    const outcome = await submit(answers);
    if (outcome === 'waitlist') {
      if (answers?.persona) setWaitlistPersona(answers.persona);
      setWaitlistOpen(true);
    }
  };

  const handleSkip = async () => {
    const outcome = await dismiss();
    if (outcome === 'waitlist') {
      setWaitlistOpen(true);
    }
  };

  return (
    <>
      <WelcomeIntroDialog
        open={open}
        onOpenChange={handleOpenChange}
        onSubmit={handleSubmit}
        onSkip={handleSkip}
      />
      <WaitlistDialog open={waitlistOpen} onOpenChange={setWaitlistOpen} userEmail={userEmail} persona={waitlistPersona} />
    </>
  );
}
